import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EStorageKeys } from '../enums/storage-keys.enum';
import { ApiResponse, ILoginResponse } from '../models/api.model';
import { IUser } from '../models/user.model';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiUrl: string = environment.apiUrl;
  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean | null>(null);
  private currentUser: BehaviorSubject<IUser | null> = new BehaviorSubject<IUser | null>(null);
  public readonly isAuthenticated$: Observable<boolean | null> = this.isAuthenticatedSubject.asObservable();
  public readonly currentUser$: Observable<IUser | null> = this.currentUser.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private storageService: StorageService
  ) {
    this.isAuthenticatedSubject.next(null);
    this.validateAuthenticationState();
  }

  private validateAuthenticationState(): void {
    const token = this.storageService.getItem(EStorageKeys.ACCESS_TOKEN);

    if (!token) {
      this.isAuthenticatedSubject.next(false);
      return;
    }
    this.getCurrentUser();
  }

  private handleAuthenticationFailure(): void {
    this.storageService.clear();
    this.currentUser.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Logs in a user with QR token
   * @param qrToken User's QR token
   * @returns Observable with login response
   */
  public loginWithQrToken(qrToken: string): Observable<ApiResponse<ILoginResponse>> {
    return this.http
      .post<ApiResponse<ILoginResponse>>(`${this.apiUrl}/auth/login`, {
        qrToken,
      })
      .pipe(
        tap((response: ApiResponse<ILoginResponse>) => {
          if (response.DATA) {
            const authData = response.DATA;
            this.storeAuthData(authData);
            this.isAuthenticatedSubject.next(true);
            this.getCurrentUser();
          }
        }),
        catchError(error => {
          this.isAuthenticatedSubject.next(false);
          return throwError(() => new Error(error.error.DATA));
        })
      );
  }

  public getAccessToken(): string {
    const accessToken = this.storageService.getItem(EStorageKeys.ACCESS_TOKEN);
    return accessToken ?? '';
  }

  public logout(): void {
    this.clearSession();
    this.router.navigate(['/login']);
  }

  /**
   * Resets the full authentication state — storage tokens AND the in-memory subjects.
   * Kept navigation-free so callers (e.g. the HTTP interceptor) can decide when/where to
   * redirect. Clearing the subjects is essential: if only storage is cleared, currentUser$
   * stays truthy and LoginComponent immediately redirects back to the landing route, which
   * re-fires the failing request and loops (see jwt.interceptor logout()).
   */
  public clearSession(): void {
    this.storageService.clear();
    this.currentUser.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Stores authentication data in storage
   * @param tokens Token response from the API
   */
  private storeAuthData(tokens: ILoginResponse): void {
    this.storeTokens(tokens);
  }

  /**
   * Stores tokens in storage
   * @param tokens Token response from the API
   */
  private storeTokens(tokens: ILoginResponse): void {
    this.storageService.setItem(EStorageKeys.ACCESS_TOKEN, tokens.accessToken);
    this.storageService.setItem(EStorageKeys.REFRESH_TOKEN, tokens.refreshToken);
  }

  /**
   * Changes the user's password
   * @param userId User ID
   * @param newPassword New password
   * @returns Observable with password change response
   */
  public changePassword(userId: string, newPassword: string): Observable<ApiResponse<any>> {
    return this.http
      .post<ApiResponse<any>>(`${this.apiUrl}/auth/change-password`, {
        userId,
        newPassword,
      })
      .pipe(
        catchError(error => {
          return throwError(() => new Error(error.error?.MESSAGE || 'Failed to change password'));
        })
      );
  }

  private getCurrentUser(): void {
    this.http
      .get<ApiResponse<IUser>>(`${this.apiUrl}/user/currentUser`)
      .pipe(
        tap((res: ApiResponse<IUser>) => {
          this.currentUser.next(res.DATA);
          this.isAuthenticatedSubject.next(true);
        }),
        catchError(error => {
          this.handleAuthenticationFailure();
          return throwError(() => error);
        })
      )
      .subscribe();
  }
}
